import React, { useState, useEffect } from 'react';
import * as yup from "yup";
import SupportBillingFormStyles from './SupportBillingFormStyles';
import useMediaQuery from '@/helpers/hooks/useMediaQuery';
import Input from "@/components/Inputs/v2/Input";
import FInput from "@components/Inputs/FInput";
import Select from "@/components/Inputs/v2/Select";
import BillingFormConfirmation from './BillingFormConfirmation';
import { Form, Formik } from "formik";
import createCase from '@/services/createCase';

const purposeArray = [
  {
    value: 'Billing Inquiry',
    label: 'Billing Inquiry',
  },
  {
    value: 'Cancel Request',
    label: 'Cancel Request',
  },
  {
    value: 'Partial Cancel Request',
    label: 'Partial Cancel Request'
  }
];

const phoneRegExp = /^(\([0-9]{3}\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$/;

const validationSchema = (cardType) =>
  yup.object().shape({
    purpose: yup
      .string()
      .min(1, "Purpose is required.")
      .required("Purpose is required."),
    first_name: yup
      .string()
      .min(1, 'Must have at least one character')
      .required('Please enter your first name'),
    last_name: yup
      .string()
      .min(1, 'Must have at least one character')
      .max(255, 'Must be shorter than 255 characters')
      .required('Please enter your last name'),
    email: yup
      .string()
      .email('Please enter a valid email address')
      .required('Please enter your email address'),
    phone: yup
      .string()
      .matches(phoneRegExp, 'Please enter a valid phone number')
      .required('Please enter your phone number'),
    description: yup
      .string()
      .min(1, 'Must have at least one character')
      .required('Please enter your description'),
  });

const SupportBilling = () => {
  const [formSuccess, setFormSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [caseErrors, setCaseErrors] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useMediaQuery('(max-width: 576px)');

  const getType = (values) => {
    if (values.purpose.includes("Cancel")) {
      return "Subscription Review"
    } else {
      return "Invoice & Payments"
    }
  }

  const handleCheckout = async (e, values, errors) => {
    e.preventDefault();
    setIsLoading(true);

    const type = getType(values);
    const name = `${values.first_name} ${values.last_name}`;

    const casePayload = {
      recordType: "012j0000000ft2uAAA", // Billing code
      status: "New",
      origin: "Web",
      description: values.description,
      subType: values.purpose,
      type: type,
      phone: values.phone,
      email: values.email,
      name: name,
    }
    const caseRes = await createCase(casePayload);
    // Set the email for the confirmation message
    if (caseRes?.data?.success) {
      setEmail(values.email);
      setFormSuccess(true);
      setCaseErrors('');
    } else {
      if (caseRes?.issue === "token") {
        setCaseErrors("Please resubmit or call the number at the top of the page");
      } else if (caseRes?.issue === "email") {
        errors.email = "We don’t recognize this email. Use the email address associated with your account."
      } else {
        setCaseErrors(caseRes?.message);
      }
    }
    setIsLoading(false);
  };

  return (
    <SupportBillingFormStyles>
      <div className="page-title">Request Billing Support</div>
      {formSuccess &&
        <BillingFormConfirmation email={email} />
      }
      {caseErrors &&
        <div className="case-errors">
          {caseErrors}
        </div>
      }
      <div className="page-description">Requesting billing support is quick and easy. Please fill out and submit the form, and we will contact you as soon as we can. </div>
      <Formik
        initialValues={{
          purpose: "",
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
          description: "",
        }}
        validationSchema={validationSchema()}
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => {
          const _ = require("lodash");
          const isDisabled = () =>
            isSubmitting ||
            !_.isEmpty(errors) ||
            !values.purpose ||
            !values.first_name ||
            !values.last_name ||
            !values.email ||
            !values.phone ||
            !values.description;

          return (
            <>
              <Form
                className="form-wrap checkout-item"
                onSubmit={(e) => handleCheckout(e, values, errors)}
              >
                <div className="row mb-2">
                  <div className="col-12">
                    <Select
                      style={{
                        borderColor:
                          errors.billing_state && touched.billing_state
                            ? "#dc3545"
                            : null,
                      }}
                      name="purpose"
                      label="Select a Purpose (Required)"
                      options={purposeArray}
                      placeholder={true}
                    />
                    {errors.purpose && touched.purpose ? (
                      <div className="error-notice">
                        {errors.purpose}
                      </div>
                    ) : null}
                  </div>
                  <div className="col-12">
                    <Input
                      style={{
                        borderColor:
                          errors.first_name && touched.first_name
                            ? "#dc3545"
                            : null,
                      }}
                      name="first_name"
                      label="First Name (Required)"
                    />
                    {errors.first_name && touched.first_name ? (
                      <div className="error-notice">
                        {errors.first_name}
                      </div>
                    ) : null}
                  </div>
                  <div className="col-12">
                    <Input
                      style={{
                        borderColor:
                          errors.last_name && touched.last_name
                            ? "#dc3545"
                            : null,
                      }}
                      name="last_name"
                      label="Last Name (Required)"
                    />
                    {errors.last_name && touched.last_name ? (
                      <div className="error-notice" >
                        {errors.last_name}
                      </div>
                    ) : null}
                  </div>
                  <div className="col-12">
                    <Input
                      style={{
                        borderColor:
                          errors.email && touched.email
                            ? "#dc3545"
                            : null,
                      }}
                      name="email"
                      label="Email (Required)"
                    />
                    {errors.email && touched.email ? (
                      <div className="error-notice">
                        {errors.email}
                      </div>
                    ) : null}
                  </div>
                  <div className="col-12">
                    <Input
                      style={{
                        borderColor:
                          errors.phone && touched.phone
                            ? "#dc3545"
                            : null,
                      }}
                      type="tel"
                      mask="(999) 999-9999"
                      maskChar={null}
                      name="phone"
                      label="Phone (Required)"
                    />
                    {errors.phone && touched.phone ? (
                      <div className="error-notice">
                        {errors.phone}
                      </div>
                    ) : null}
                  </div>
                  <div className="col-12 description-input">
                    <FInput
                      style={{
                        borderColor:
                          errors.phone && touched.phone
                            ? "#dc3545"
                            : null,
                      }}
                      type="textarea"
                      className=""
                      name="description"
                      label="What can we help you with? (Required)"
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      id="description"
                      rows="3"
                      values={values}
                      touched={touched}
                      errors={errors}
                      required
                    />
                  </div>
                </div>
                <div className="btn-wrap">
                  <button
                    className={`btn btn-primary ${isDisabled() &&
                      "disabled"}`}
                    disabled={isDisabled()}
                    type="submit"
                  >
                    Send Message
                  </button>
                </div>

              </Form>
            </>
          );
        }}
      </Formik>
      {isLoading && (
        <div className="is-loading">
        </div>
      )}
    </SupportBillingFormStyles >
  );
};

export default SupportBilling;